import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import { MintingEnum } from 'modules/Room/game-status/constants/game-status-enum';
import { useGameStatusStore } from 'modules/Room/game-status/store/game-status-store';

import { InfoBannerType } from 'shared/components/ui/InfoBanner/InfoBanner';
import { MoveStatusRenderer } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { SOCIAL_MEDIA } from 'shared/constants/socialMedia';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';
import { useRoomStore } from 'shared/store/roomStore';

import IconDiscord from 'assets/discord.svg?react';
import IconTelegram from 'assets/telegram.svg?react';
import IconX from 'assets/x.svg?react';

interface INoOneJoinStatusProps {
  isInvited?: boolean;
}

export const NoOneJoinStatus = ({ isInvited }: INoOneJoinStatusProps) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { chainId, address } = useAccount();
  const { activeRoomId } = useParams();
  const isMobile = useMediaQuery('(max-width: 1199px)');

  const { noOneJoinHash } = usePlayerGamesStore();
  const { setMintingLoader, setMintingHash } = useGameStatusStore();
  const publicClient = usePublicClient();
  const txRef = useRef<`0x${string}`>();

  const walletClient = useWalletClient();
  const { exitRoomAction } = useRoomStore();

  useEffect(() => {
    audioManager.playSound(AUDIO_TYPES.CONFIRM_MOVE);
  }, []);

  useEffect(() => {
    if (chainId && noOneJoinHash?.[chainId!]?.hash) {
      txRef.current = noOneJoinHash[chainId!]?.hash;
      setMintingHash(txRef.current);
      setMintingLoader(MintingEnum.TAKE_MONEY_BACK);
    }
  }, []);

  const handleExit = async () => {
    if (loading || !activeRoomId) return;
    setLoading(true);
    await exitRoomAction({
      chainId,
      publicClient,
      roomId: BigInt(activeRoomId),
      userAddress: address,
      walletClient,
    });
    setLoading(false);
  };

  const handleClick = (url: string) => () => {
    window.open(url, '_blank');
  };

  return (
    <div className="mb-4">
      <MoveStatusRenderer
        title={
          isInvited ? t('modals.noOneJoin.opponentDoesntAccept') : t('modals.noOneJoin.situation')
        }
        type={InfoBannerType.WARNING}
      />

      <div className="rounded-xl bg-deep-blue bg-opacity-70 p-4">
        {!isInvited && (
          <p className="mb-5 text-justify text-sm text-white sm:text-base">
            {t('modals.noOneJoin.unpopularBet')}
          </p>
        )}

        <p className="mb-[24px] text-justify text-sm text-white sm:text-base">
          {t('modals.noOneJoin.proTip')}
        </p>

        <SecondaryBtn
          className={`mb-4 w-full ${isMobile ? 'hidden' : ''}`}
          text={'modals.takeABetBack.collectButton'}
          onClick={handleExit}
          isLoading={loading}
        />

        <div className={`mb-4 h-[1px] w-full bg-border ${isMobile ? 'hidden' : ''}`}></div>
        <div className="flex w-full justify-around gap-4">
          <button
            className="flex justify-center rounded-xl border border-[#424269] bg-[linear-gradient(91.27deg,#1B4563_-6.69%,#151552_106.59%)] p-4 lg:grow"
            onClick={handleClick(SOCIAL_MEDIA.TELEGRAM)}
          >
            <IconTelegram className="h-auto w-4 md:w-7" />
          </button>
          <button
            className="flex justify-center rounded-xl border border-[#424269] bg-[linear-gradient(91.27deg,#040435_-6.69%,#151552_106.59%)] p-4 lg:grow"
            onClick={handleClick(SOCIAL_MEDIA.TWITTER)}
          >
            <IconX className="h-auto w-4 md:w-[28px]" />
          </button>
          <button
            className="flex justify-center rounded-xl border border-[#424269] bg-[linear-gradient(91.27deg,#352372_-6.69%,#151552_106.59%)] p-4 lg:grow"
            onClick={handleClick(SOCIAL_MEDIA.DISCORD)}
          >
            <IconDiscord className="h-auto w-4 md:w-7" />
          </button>
        </div>
      </div>
    </div>
  );
};
